interface DefaultErrorFallbackProps {
  error: Error;
  retry: () => void;
  retryCount: number;
  level?: 'page' | 'section' | 'component';
}

export function DefaultErrorFallback({
  error,
  retry,
  retryCount,
  level = 'component',
}: DefaultErrorFallbackProps) {
  const isPageLevel = level === 'page';
  const canRetry = retryCount < 3;

  return (
    <div
      className={`flex flex-col items-center justify-center p-8 ${isPageLevel ? 'min-h-[50vh]' : 'min-h-[200px]'}`}
    >
      <div className="text-center max-w-md mx-auto">
        <div className="mb-4">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.268 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
        </div>

        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {isPageLevel ? 'Page Error' : 'Something went wrong'}
        </h2>

        <p className="text-gray-600 mb-4">
          {isPageLevel
            ? 'We encountered an error while loading this page.'
            : 'This section encountered an unexpected error.'}
        </p>

        {process.env.NODE_ENV === 'development' && (
          <details className="mb-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
              Error Details (Development)
            </summary>
            <pre className="mt-2 p-3 bg-gray-100 rounded text-xs overflow-auto max-h-32">
              {error.stack}
            </pre>
          </details>
        )}

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {canRetry && (
            <button
              onClick={retry}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again {retryCount > 0 && `(${3 - retryCount} left)`}
            </button>
          )}

          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Refresh Page
          </button>
        </div>

        {!canRetry && (
          <p className="mt-4 text-sm text-gray-500">
            If this problem persists, please contact support.
          </p>
        )}
      </div>
    </div>
  );
}
